import * as Sentry from '@sentry/react';

export default class DataLayer {
  /**
   * Get data from local storage
   *
   * @returns {object}
   */
  getData(): Record<string, unknown> {
    return JSON.parse(window.localStorage.getItem('wypDatalayer')) || [];
  }

  /**
   * Clear data from local storage
   *
   * @returns {void}
   */
  clearData(): void {
    window.localStorage.removeItem('wypDataLayer');
  }

  /**
   * Clear profile data from data layer
   *
   * @returns {void}
   */
  removeProperty(params: Array<string>): void {
    if (window.dataLayer !== 'undefined') {
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      window.dataLayer = window?.dataLayer?.filter(data => !params.includes(data.event));
    }
  }

  /**
   * Push data object to GA DataLayer
   *
   * @param {object} data
   */
  toDataLayer(data: any): Record<string, unknown> {
    if (!('dataLayer' in window)) {
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      return console.error('Datalayer does not exist!');
    }

    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    window.dataLayer.push(data);
  }

  /**
   * Push data object to Sentry
   *
   * @param {object} data
   */
  toSentry(data: any): Record<string, unknown> {
    if (!('Raven' in window)) {
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      return console.error('Raven does not exist!');
    }

    Sentry.captureMessage('Data push', {
      level: 'info',
      extra: {
        ...data,
      },
    });
  }

  /**
   * Push data to local storage
   *
   * @param {object} data
   * @returns {void}
   */
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  push(data: any): Record<string, unknown> {
    if ('localStorage' in window) {
      const localData = this.getData();

      // push data to our localstorage
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      localData.push(data);
      window.localStorage.setItem('wypDataLayer', JSON.stringify(localData));
    }

    this.toDataLayer(data);
  }

  gtag() {
    if (!('dataLayer' in window)) {
      return console.error('Datalayer does not exist!');
    }

    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  }
}

/**
 * Gtag consent properties interface
 * All seven consent properties required for Google Tag Manager consent mode
 */
export interface GtagConsentProperties {
  ad_user_data: 'granted' | 'denied';
  ad_personalization: 'granted' | 'denied';
  ad_storage: 'granted' | 'denied';
  analytics_storage: 'granted' | 'denied';
  functionality_storage: 'granted' | 'denied';
  personalization_storage: 'granted' | 'denied';
  security_storage: 'granted' | 'denied';
}

/**
 * Cookie consent value type
 */
export type CookieConsentValue = 'accepted' | 'rejected' | 'closed' | null;

/**
 * Set default gtag consent before GTM loads
 * This must be called in the HTML template before GTM initialization
 * to ensure consent is set before any tracking scripts execute
 *
 * IMPORTANT: Core functionality cookies are exempt from gtag consent restrictions
 * and will continue to work regardless of consent status, as they are required
 * for core site functionality. These cookies include:
 * - Selfie verification cookies (required for user verification)
 * - VWO cookies (required for A/B testing and site optimization)
 * - MixPanel cookies (required for analytics and user behavior tracking)
 * - Vysion cookies (required for fraud prevention and security)
 *
 * These cookies are not affected by gtag consent settings and will always be set
 * to ensure the site functions properly.
 *
 * @param consentValue - 'granted' or 'denied' for all consent properties
 */
export const setDefaultGtagConsent = (consentValue: 'granted' | 'denied'): void => {
  if (typeof window === 'undefined' || !('dataLayer' in window)) {
    return;
  }

  const consentProperties: GtagConsentProperties = {
    ad_user_data: consentValue,
    ad_personalization: consentValue,
    ad_storage: consentValue,
    analytics_storage: consentValue,
    functionality_storage: consentValue,
    personalization_storage: consentValue,
    security_storage: consentValue,
  };

  // Use gtag('consent', 'default', {...}) to set consent before GTM loads
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  window.dataLayer = window.dataLayer || [];

  const dataLayer = new DataLayer();

  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  dataLayer.gtag('consent', 'default', consentProperties);
};

/**
 * Update gtag consent for cookie consent management
 * This function updates consent after GTM has loaded and should be called
 * when user makes a consent choice or when consent state changes
 *
 * IMPORTANT: Core functionality cookies are exempt from gtag consent restrictions
 * and will continue to work regardless of consent status, as they are required
 * for core site functionality. These cookies include:
 * - Selfie verification cookies (required for user verification)
 * - VWO cookies (required for A/B testing and site optimization)
 * - MixPanel cookies (required for analytics and user behavior tracking)
 * - Vysion cookies (required for fraud prevention and security)
 *
 * These cookies are not affected by gtag consent settings and will always be set
 * to ensure the site functions properly.
 *
 * @param consentType - 'granted' or 'denied'
 */
export const updateCookieConsent = (consentType: 'granted' | 'denied'): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // Queue consent update event
  // GTM/gtag will process queued `dataLayer` events once loaded.
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  window.dataLayer = window.dataLayer || [];

  const dataLayer = new DataLayer();

  // Push GTM event for consent update
  const eventName = consentType === 'granted' ? 'cookie_consent.update.granted' : 'cookie_consent.update.denied';

  dataLayer.push({
    event: eventName,
  });
};

/**
 * Update consent based on user qualification and cookie consent preference
 * This implements the business logic for Google Consent Mode v2:
 * - CPPA-qualified users (e.g., California): Respect cookie consent preference
 * - Non-qualified users: Always grant consent
 *
 * IMPORTANT: Must be called on the SAME page where consent changes, before any page navigation
 *
 * @param isQualified - Whether user is qualified for CPPA compliance (from useCookieConsentCheck hook)
 * @param cookieConsent - Current cookie consent value ('accepted', 'rejected', 'closed', or null)
 * @returns void
 */
export const updateConsentBasedOnLocation = (
  isQualified: boolean | undefined,
  cookieConsent: CookieConsentValue
): void => {
  if (typeof window === 'undefined' || isQualified === undefined) {
    return;
  }

  // Handle different scenarios
  if (!isQualified) {
    // NOT qualified (not in CPPA regions) - grant everything regardless of cookie consent
    updateCookieConsent('granted');
  } else {
    // Qualified for CPPA (e.g., California) - respect cookie consent
    if (cookieConsent === 'accepted') {
      updateCookieConsent('granted');
    } else if (cookieConsent === 'rejected') {
      updateCookieConsent('denied');
    }
    // If cookieConsent is 'not set' or 'closed', the default 'denied' from index.html remains
  }
};
